/**
 * @fileoverview Main application component with routing and authentication.
 *
 * Configures React Router with protected routes and authentication provider.
 * Uses React.lazy for code splitting by route.
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, ToastProvider } from './contexts';
import { ProtectedRoute, ToastContainer, Spinner } from './components';

// Lazy load page components for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite').then(m => ({ default: m.AcceptInvite })));
const CaseWorkspace = lazy(() => import('./pages/CaseWorkspace').then(m => ({ default: m.CaseWorkspace })));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard').then(m => ({ default: m.AdminDashboard })));
const AdminUsers = lazy(() => import('./pages/admin/Users').then(m => ({ default: m.Users })));
const AdminInvites = lazy(() => import('./pages/admin/Invites').then(m => ({ default: m.Invites })));
const AdminCaseStudies = lazy(() => import('./pages/admin/CaseStudies').then(m => ({ default: m.AdminCaseStudies })));
const AdminSettings = lazy(() => import('./pages/admin/Settings').then(m => ({ default: m.Settings })));
const CaseWizard = lazy(() => import('./pages/CaseWizard').then(m => ({ default: m.CaseWizard })));
const Profile = lazy(() => import('./pages/Profile').then(m => ({ default: m.Profile })));

/**
 * Page loading fallback component displayed during lazy load.
 *
 * @returns Loading spinner centered on screen
 */
function PageLoadingFallback(): JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900">
      <Spinner size="lg" />
    </div>
  );
}

/**
 * Main application component with routing configuration.
 *
 * Wraps the application with AuthProvider for authentication state,
 * and configures routes with appropriate protection.
 * Uses Suspense for lazy-loaded route components.
 *
 * @returns The root App component with router setup
 */
export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
        <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          {/* Case creation wizard */}
          <Route
            path="/cases/new"
            element={
              <ProtectedRoute>
                <CaseWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cases/new/step/:step"
            element={
              <ProtectedRoute>
                <CaseWizard />
              </ProtectedRoute>
            }
          />

          {/* Resume draft case */}
          <Route
            path="/cases/new/:caseId"
            element={
              <ProtectedRoute>
                <CaseWizard />
              </ProtectedRoute>
            }
          />

          {/* Case workspace */}
          <Route
            path="/cases/:caseId"
            element={
              <ProtectedRoute>
                <CaseWorkspace />
              </ProtectedRoute>
            }
          />

          {/* User profile */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />

          {/* Admin routes - require admin role */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <AdminUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/invites"
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <AdminInvites />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/case-studies"
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <AdminCaseStudies />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <AdminSettings />
              </ProtectedRoute>
            }
          />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* 404 fallback */}
          <Route
            path="*"
            element={
              <div className="flex min-h-screen items-center justify-center bg-slate-900">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-white">404</h1>
                  <p className="mt-2 text-slate-400">Page not found</p>
                </div>
              </div>
            }
          />
        </Routes>
        </Suspense>
        <ToastContainer />
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
